// bluaIQ — M15: Project Contacts Directory
// Manage contacts: suppliers, contractors, inspectors, architects, etc.

import { apiFetch } from '../utils/api'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Phone, Mail, Search, Users, Trash2, MessageSquare, UserPlus, Download, Pencil } from 'lucide-react'
import { downloadCsv } from '../utils/exportCsv'
import { isDemoProject, DEMO_CONTACTS } from '../utils/demoData'

// ── TYPES ─────────────────────────────────────────

interface Contact {
  id: string
  name: string
  role: string
  phone?: string
  email?: string
  whatsapp?: string
  company?: string
  trade?: string
  notes?: string
  rating?: number | null
}

const ROLE_CONFIG: Record<string, { en: string; es: string; color: string; emoji: string }> = {
  SUPPLIER:    { en: 'Supplier',    es: 'Proveedor',           color: '#3B82F6', emoji: '🏪' },
  CONTRACTOR:  { en: 'Contractor',  es: 'Contratista',         color: '#14B8A6', emoji: '🔨' },
  INSPECTOR:   { en: 'Inspector',   es: 'Inspector',           color: '#00BCD4', emoji: '🔍' },
  ARCHITECT:   { en: 'Architect',   es: 'Arquitecto',          color: '#F59E0B', emoji: '📐' },
  REALTOR:     { en: 'Realtor',     es: 'Agente Inmobiliario',  color: '#EC4899', emoji: '🏠' },
  LENDER:      { en: 'Lender',      es: 'Prestamista',         color: '#06B6D4', emoji: '🏦' },
  OTHER:       { en: 'Other',       es: 'Otro',                color: '#9E9890', emoji: '📋' },
}

// ── COMPONENT ─────────────────────────────────────

export default function ContactsModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'
  // Phase 1 — Material Orders return context. When this page is
  // opened via the "Add supplier" link in MaterialOrderSchedule,
  // the URL carries `?from=material-orders&category=<cat>`. We
  // surface a small back pill that re-enters Material Orders with
  // the category preserved. No PII travels in the URL.
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const fromParam = searchParams.get('from')
  const categoryParam = searchParams.get('category')
  // Supplier-selection handoff (FE-only).
  // When this page is opened via the "+ Add supplier" CTA inside a
  // specific Material Order, the URL also carries `&orderId=<id>`.
  // Each contact card then renders a bilingual "Use this supplier"
  // button that navigates back to the originating order with
  //   ?selectedSupplierId=<contact.id>&orderId=<orderIdParam>
  //   [&category=<categoryParam>]
  // The MO side consumes those params and applies the picked
  // supplier to LOCAL FE state only — no backend, no auto-order.
  // When `orderIdParam` is null (the user arrived via the sidebar
  // gate, PR #174), the helper-text card from PR #173 is still
  // shown but no "Use this supplier" button is rendered, because
  // there is no specific order to attach the contact to.
  const orderIdParam = searchParams.get('orderId')
  const showBackToMaterialOrders = fromParam === 'material-orders' && !!projectId
  const showUseAsSupplier = showBackToMaterialOrders && !!orderIdParam
  const backToMaterialOrdersHref = projectId
    ? `/projects/${projectId}/material-orders${categoryParam ? `?category=${encodeURIComponent(categoryParam)}` : ''}`
    : '#'
  // Build the project-scoped URL that hands a selected contact
  // back to the originating Material Order. Pure URL construction
  // — never called when `orderIdParam` is null because the button
  // is gated by `showUseAsSupplier`.
  const buildUseAsSupplierHref = (contactId: string): string => {
    if (!projectId || !orderIdParam) return '#'
    const params = new URLSearchParams({
      selectedSupplierId: contactId,
      orderId: orderIdParam,
    })
    if (categoryParam) params.set('category', categoryParam)
    return `/projects/${projectId}/material-orders?${params.toString()}`
  }
  const [search, setSearch] = useState('')
  const [filterRole, setFilterRole] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingContact, setEditingContact] = useState<Contact | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  const startEdit = (contact: Contact) => {
    setEditingContact(contact)
    setShowForm(true)
  }

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  const { data: apiContacts, isLoading, isError } = useQuery<Contact[]>({
    queryKey: ['contacts', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/contacts`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })

  // PR-187: demo fallback gated to demo projects only.
  // Real projects: API success → real contacts; API error or invalid
  // response → [] (honest error state rendered separately below).
  // Demo projects: keep DEMO_CONTACTS fallback so the guided tour
  // still works when the demo API call fails or returns nothing.
  const isDemo = isDemoProject(projectId)
  const contacts: Contact[] = isDemo
    ? (Array.isArray(apiContacts) ? apiContacts : DEMO_CONTACTS)
    : (Array.isArray(apiContacts) ? apiContacts : [])

  // Show an error card for real projects only — never for demo.
  const showApiError = !isDemo && !isLoading && isError

  const deleteMutation = useMutation({
    mutationFn: async (contactId: string) => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/contacts/${contactId}`, { method: 'DELETE', headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }) },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contacts', projectId] })
  })

  const filtered = contacts?.filter(c => {
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.company?.toLowerCase().includes(search.toLowerCase())
    const matchRole = !filterRole || c.role === filterRole
    return matchSearch && matchRole
  }) || []

  const roles = [...new Set(contacts?.map(c => c.role) || [])]

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4 relative">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-[#1C1A17] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2">
          🚧 {toast}
        </div>
      )}
      {/* Phase 1 — Back-to-Material-Orders pill. Renders only when
          the URL carries `?from=material-orders`. Quiet styling —
          not a primary CTA. The href echoes `?category=…` so the
          user lands on the same scoped view they came from. */}
      {showBackToMaterialOrders && (
        <Link
          to={backToMaterialOrdersHref}
          data-testid="contacts-back-to-material-orders"
          className="inline-flex items-center gap-1 text-[11px] font-[700] text-[#06539B] hover:underline"
        >
          {isEs ? '← Volver a Pedidos de Material' : '← Back to Material Orders'}
        </Link>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{t('Contacts')}</h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">{contacts?.length || 0} {t('contacts.label')}</p>
        </div>
        <div className="flex items-center gap-2">
          {contacts && contacts.length > 0 && (
            <button
              onClick={() => downloadCsv({
                filename: `contactos-proyecto-${projectId}`,
                headers: [isEs ? 'Nombre' : 'Name', isEs ? 'Rol' : 'Role', isEs ? 'Empresa' : 'Company', isEs ? 'Oficio' : 'Trade', isEs ? 'Teléfono' : 'Phone', 'Email'],
                rows: contacts.map(c => [c.name, c.role, c.company || '', c.trade || '', c.phone || '', c.email || ''])
              })}
              className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors"
            >
              <Download size={14} />
              CSV
            </button>
          )}
          <button onClick={() => { setEditingContact(null); setShowForm(true) }}
            className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full">
            <Plus size={14} /> {t('Add Contact')}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9E9890]" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('Search contacts...')}
          className="w-full border border-[#ECEAE4] rounded-full pl-9 pr-4 py-2.5 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
      </div>

      {/* Role filter */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilterRole(null)}
          className={`text-[10px] font-[800] px-3 py-1.5 rounded-full border transition-colors ${!filterRole ? 'bg-[#06539B] text-white border-[#06539B]' : 'border-[#ECEAE4] text-[#9E9890]'}`}>
          {t('All')}
        </button>
        {Object.entries(ROLE_CONFIG).map(([key, config]) => (
          <button key={key} onClick={() => setFilterRole(key)}
            className={`text-[10px] font-[800] px-3 py-1.5 rounded-full border transition-colors ${filterRole === key ? 'bg-[#06539B] text-white border-[#06539B]' : 'border-[#ECEAE4] text-[#9E9890]'}`}>
            {config.emoji} {isEs ? config.es : config.en}
          </button>
        ))}
      </div>

      {/* Add / Edit form.
          Key on editingContact?.id so React remounts the form
          whenever the editing target switches — critical because
          the form uses a lazy useState initializer that only runs
          once per mount. Without this key, switching from Add →
          Edit (or Edit A → Edit B) leaves stale form state, which
          is the root cause of the "create/edit doesn't work"
          report (saves submit the wrong contact's data). */}
      {showForm && (
        <NewContactForm
          key={editingContact?.id ?? 'new'}
          projectId={projectId!}
          isEs={isEs}
          editingContact={editingContact}
          onClose={() => { setShowForm(false); setEditingContact(null) }}
          onSaved={({ wasEditing }) => {
            setShowForm(false)
            setEditingContact(null)
            queryClient.invalidateQueries({ queryKey: ['contacts', projectId] })
            showToast(
              wasEditing
                ? (isEs ? 'Contacto actualizado' : 'Contact updated')
                : (isEs ? 'Contacto guardado' : 'Contact saved')
            )
          }}
        />
      )}

      {/* Contact list */}
      <div className="space-y-2">
        {filtered.map(contact => {
          const config = ROLE_CONFIG[contact.role]
          // PR #178: only SUPPLIER-role contacts can be applied as material-order
          // suppliers. Non-SUPPLIER contacts in MO context show a role-mismatch
          // helper instead of the "Use this supplier" button.
          const isSupplierRole = (contact.role || '').toUpperCase() === 'SUPPLIER'
          return (
            <div key={contact.id} className="bg-white border border-[#ECEAE4] rounded-xl hover:border-[#06539B] transition-colors p-3">
              <div className="flex items-center gap-3">
                <span className="text-[16px]">{config?.emoji || '📋'}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-[800] text-[#1C1A17]">{contact.name}</span>
                    <span className="text-[9px] font-[800] px-2 py-0.5 rounded-full text-white" style={{ background: config?.color || '#9E9890' }}>
                      {isEs ? config?.es : config?.en || contact.role}
                    </span>
                    {contact.rating != null && contact.rating > 0 && (
                      <span
                        className="text-[9px] font-[800] px-2 py-0.5 rounded-full bg-amber-50"
                        style={{ color: '#F59E0B' }}
                        title={isEs ? `Calificación: ${contact.rating}/5` : `Rating: ${contact.rating}/5`}
                      >
                        {'★'.repeat(contact.rating)}{'☆'.repeat(5 - contact.rating)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    {contact.company && <span className="text-[10px] font-[600] text-[#9E9890]">{contact.company}</span>}
                    {contact.trade && <span className="text-[10px] font-[600] text-[#9E9890]">· {contact.trade}</span>}
                  </div>
                  {contact.notes && (
                    <p className="text-[10px] font-[600] text-[#9E9890] mt-1 italic">
                      {contact.notes}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => startEdit(contact)}
                    className="text-[#9E9890] hover:text-[#06539B] transition-colors"
                    title={isEs ? 'Editar' : 'Edit'}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(isEs ? '¿Eliminar este contacto?' : 'Delete this contact?')) {
                        deleteMutation.mutate(contact.id)
                      }
                    }}
                    className="text-[#9E9890] hover:text-red-600 transition-colors"
                    title={isEs ? 'Eliminar' : 'Delete'}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {/* Action buttons.
                  Material-Orders context (interim fix): when the URL
                  carries `?from=material-orders` (PR #167), the Email /
                  SMS / WhatsApp pills are replaced with a quiet
                  bilingual info row that points the user back to the
                  Material Orders schedule. The previous render let a
                  user clicking "Email" trigger a `mailto:` protocol
                  jump (blank tab on desktops without a mail handler)
                  and the WhatsApp pill's `target="_blank"` opened a
                  new tab to `wa.me/<digits>` — both of which surfaced
                  as a "blank page I can't return from" symptom.
                  Suppressing those handlers in this context closes the
                  blank-page regression without changing any other
                  Contacts behavior. The Edit + Delete icons (above) and
                  the existing back-pill (top of page) remain available.
                  Normal Contacts (no `?from=material-orders`) renders
                  the full Email/SMS/WhatsApp/Invite-portal row exactly
                  as before. */}
              {showBackToMaterialOrders ? (
                <div className="mt-2 space-y-2">
                  <div
                    data-testid="contacts-material-orders-helper"
                    className="flex items-start gap-2 rounded-lg border border-dashed border-[#ECEAE4] bg-[#FAFAF8] px-3 py-2 text-[10px] font-[700] text-[#6B6660] leading-snug"
                  >
                    <UserPlus size={12} className="mt-[1px] flex-shrink-0 text-[#9E9890]" />
                    <span>
                      {showUseAsSupplier
                        ? (isSupplierRole
                            ? (isEs
                                ? 'Elige este contacto como proveedor para el pedido de material.'
                                : 'Pick this contact as the supplier for the material order.')
                            : (isEs
                                ? 'Este contacto debe tener el rol Proveedor antes de usarse en un pedido de material.'
                                : 'This contact must have the Supplier role before it can be used for a material order.'))
                        : (isEs
                            ? 'Para aplicar un proveedor a un pedido específico, vuelve a Pedidos de Material, abre ese pedido y usa “Agregar o elegir proveedor en Contactos”.'
                            : 'To apply a supplier to a specific order, return to Material Orders, expand that order, and use “Add or choose supplier in Contacts”.')}
                    </span>
                  </div>
                  {showUseAsSupplier && isSupplierRole && (
                    <button
                      type="button"
                      data-testid={`contacts-use-as-supplier-${contact.id}`}
                      onClick={() => navigate(buildUseAsSupplierHref(contact.id))}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#06539B] px-3 py-1.5 text-[10px] font-[800] text-white hover:bg-[#14457E]"
                    >
                      <UserPlus size={12} />
                      {isEs ? 'Usar este proveedor' : 'Use this supplier'}
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {contact.email && (
                    <a
                      href={`mailto:${contact.email}`}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#ECEAE4] text-[10px] font-[800] text-[#1C1A17] hover:bg-[#F2F0EB] transition-colors"
                      title={t('contacts.actions.sendEmail')}
                    >
                      <Mail size={12} />
                      {t('contacts.actions.email')}
                    </a>
                  )}
                  {contact.phone && (
                    <a
                      href={`sms:${contact.phone}`}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#ECEAE4] text-[10px] font-[800] text-[#1C1A17] hover:bg-[#F2F0EB] transition-colors"
                      title={t('contacts.actions.sendSms')}
                    >
                      <MessageSquare size={12} />
                      {t('contacts.actions.sms')}
                    </a>
                  )}
                  {(contact.whatsapp || contact.phone) && (
                    <a
                      href={`https://wa.me/${(contact.whatsapp || contact.phone || '').replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#ECEAE4] text-[10px] font-[800] text-[#1C1A17] hover:bg-[#F2F0EB] transition-colors"
                      title={t('contacts.actions.sendWhatsapp')}
                    >
                      <Phone size={12} />
                      WhatsApp
                    </a>
                  )}
                  {/* Client Portal is not sold (Blu 2026-10-21) — no portal invite here. */}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* PR-187: API error state — real projects only, never demo */}
      {showApiError && (
        <div className="text-center py-12" data-testid="contacts-api-error">
          <Users size={36} className="mx-auto text-[#ECEAE4] mb-3" />
          <p className="text-[12px] font-[700] text-[#9E9890]">
            {isEs
              ? 'No se pudieron cargar los contactos. Intenta de nuevo.'
              : "Couldn't load contacts. Please refresh."}
          </p>
        </div>
      )}
      {/* Suppress the empty state while the error card is showing */}
      {filtered.length === 0 && !isLoading && !showApiError && (
        <div className="text-center py-12">
          <Users size={36} className="mx-auto text-[#ECEAE4] mb-3" />
          <p className="text-[12px] font-[700] text-[#9E9890]">{t('No contacts found')}</p>
        </div>
      )}
    </div>
  )
}

// ── NEW CONTACT FORM ──────────────────────────────

function NewContactForm({ projectId, isEs, editingContact, onClose, onSaved }: {
  projectId: string
  isEs: boolean
  editingContact: Contact | null
  onClose: () => void
  onSaved: (args: { wasEditing: boolean }) => void
}) {
  const { getToken } = useAuth()
  const { t } = useTranslation()
  const initialForm = { name: '', role: 'CONTRACTOR', phone: '', email: '', whatsapp: '', company: '', trade: '', notes: '', rating: null as number | null }
  const [form, setForm] = useState(() => editingContact ? {
    name: editingContact.name || '',
    role: editingContact.role || 'CONTRACTOR',
    phone: editingContact.phone || '',
    email: editingContact.email || '',
    whatsapp: editingContact.whatsapp || '',
    company: editingContact.company || '',
    trade: editingContact.trade || '',
    notes: editingContact.notes || '',
    rating: editingContact.rating ?? null,
  } : initialForm)

  // WhatsApp same-as-phone toggle. Most construction contacts
  // use the same number for SMS and WhatsApp, so the separate
  // field is noise by default. Defaults:
  //   - new contact      → checked (whatsapp = phone on submit)
  //   - existing contact → checked when no whatsapp on file OR
  //     whatsapp matches phone; unchecked when whatsapp differs
  //     from phone (preserve the separate number).
  const [usePhoneForWhatsapp, setUsePhoneForWhatsapp] = useState(() => {
    if (!editingContact) return true
    if (!editingContact.whatsapp) return true
    return editingContact.whatsapp === editingContact.phone
  })

  const [formError, setFormError] = useState<string | null>(null)
  const editingId = editingContact?.id ?? null

  // Strip empty/whitespace strings → undefined so backend Zod optionals
  // (.email(), .min(1), etc.) don't reject "" as an invalid value.
  const cleanString = (s?: string) => {
    const trimmed = s?.trim()
    return trimmed ? trimmed : undefined
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        throw new Error(t('contacts.validation.invalidEmail'))
      }
      const cleanedPhone = cleanString(form.phone)
      const cleanedWhatsapp = usePhoneForWhatsapp
        ? cleanedPhone
        : cleanString(form.whatsapp)
      const payload: any = {
        name: cleanString(form.name),
        role: form.role,
        phone: cleanedPhone,
        email: cleanString(form.email),
        whatsapp: cleanedWhatsapp,
        company: cleanString(form.company),
        trade: cleanString(form.trade),
        notes: cleanString(form.notes),
      }
      // Only send rating if it's set; omit otherwise so backend can leave it untouched
      if (form.rating !== null) payload.rating = form.rating

      const url = editingId
        ? `/api/projects/${projectId}/contacts/${editingId}`
        : `/api/projects/${projectId}/contacts`
      const method = editingId ? 'PATCH' : 'POST'

      const res = await apiFetch(url, {
        method,
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || err.message || (isEs ? 'No se pudo guardar el contacto' : 'Could not save contact'))
      }
      return res.json()
    },
    onSuccess: () => { setFormError(null); onSaved({ wasEditing: !!editingId }) },
    onError: (error: Error) => { setFormError(error.message) },
  })

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[14px] font-[900] tracking-[-0.3px] text-[#1C1A17]">
          {editingId
            ? (isEs ? 'Editar Contacto' : 'Edit Contact')
            : (isEs ? 'Agregar Contacto' : 'Add Contact')}
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Nombre */}
        <div>
          <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">
            {isEs ? 'Nombre' : 'Name'}
          </label>
          <input
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder={isEs ? 'Nombre completo' : 'Full name'}
            className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
          />
        </div>

        {/* Tipo / Role */}
        <div>
          <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">
            {isEs ? 'Tipo' : 'Type'}
          </label>
          <select
            value={form.role}
            onChange={e => setForm({ ...form, role: e.target.value })}
            className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
          >
            <option value="CONTRACTOR">{isEs ? 'Contratista' : 'Contractor'}</option>
            <option value="SUPPLIER">{isEs ? 'Proveedor' : 'Supplier'}</option>
            <option value="INSPECTOR">{isEs ? 'Inspector' : 'Inspector'}</option>
            <option value="ARCHITECT">{isEs ? 'Arquitecto' : 'Architect'}</option>
            <option value="REALTOR">{isEs ? 'Agente Inmobiliario' : 'Realtor'}</option>
            <option value="LENDER">{isEs ? 'Prestamista' : 'Lender'}</option>
            <option value="OTHER">{isEs ? 'Otro' : 'Other'}</option>
          </select>
        </div>

        {/* Email */}
        <div>
          <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">
            {isEs ? 'Correo' : 'Email'}
          </label>
          <input
            type="email"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="correo@ejemplo.com"
            className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
          />
        </div>

        {/* Teléfono */}
        <div>
          <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">
            {isEs ? 'Teléfono' : 'Phone'}
          </label>
          <input
            type="tel"
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
            placeholder="+1 555 123 4567"
            className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
          />
        </div>

        {/* WhatsApp — same-as-phone toggle.
            Checkbox controls whether we submit `whatsapp = phone`
            or a separate value. The separate input only renders
            when the box is unchecked, keeping the form clean for
            the common "phone = whatsapp" case. */}
        <div>
          <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">
            WhatsApp
          </label>
          <label className="flex items-center gap-2 h-[38px] cursor-pointer">
            <input
              type="checkbox"
              checked={usePhoneForWhatsapp}
              onChange={e => setUsePhoneForWhatsapp(e.target.checked)}
              className="rounded border-[#ECEAE4] text-[#06539B] focus:ring-[#06539B]"
            />
            <span className="text-[11px] font-[600] text-[#1C1A17]">
              {isEs ? 'Usar este número para WhatsApp' : 'Use this number for WhatsApp'}
            </span>
          </label>
        </div>

        {!usePhoneForWhatsapp && (
          <div>
            <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">
              {isEs ? 'Número de WhatsApp' : 'WhatsApp number'}
            </label>
            <input
              type="tel"
              value={form.whatsapp}
              onChange={e => setForm({ ...form, whatsapp: e.target.value })}
              placeholder="+1 555 123 4567"
              className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
          </div>
        )}

        {/* Empresa */}
        <div>
          <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">
            {isEs ? 'Empresa' : 'Company'}
          </label>
          <input
            value={form.company}
            onChange={e => setForm({ ...form, company: e.target.value })}
            placeholder={isEs ? 'Nombre de la empresa' : 'Company name'}
            className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
          />
        </div>

        {/* Oficio / Trade */}
        <div>
          <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">
            {isEs ? 'Oficio' : 'Trade'}
          </label>
          <input
            value={form.trade}
            onChange={e => setForm({ ...form, trade: e.target.value })}
            placeholder={isEs ? 'Plomería, electricidad, etc.' : 'Plumbing, electrical, etc.'}
            className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
          />
        </div>

        {/* Calificación / Rating — selector visual de 5 estrellas */}
        <div>
          <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">
            {isEs ? 'Calificación' : 'Rating'}
          </label>
          <div className="flex items-center gap-1 h-[38px]">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                type="button"
                onClick={() => setForm({ ...form, rating: form.rating === star ? null : star })}
                className="text-[20px] leading-none transition-colors"
                title={isEs ? `${star} estrella${star > 1 ? 's' : ''}` : `${star} star${star > 1 ? 's' : ''}`}
              >
                <span style={{ color: form.rating !== null && star <= form.rating ? '#F59E0B' : '#ECEAE4' }}>
                  ★
                </span>
              </button>
            ))}
            {form.rating !== null && (
              <button
                type="button"
                onClick={() => setForm({ ...form, rating: null })}
                className="ml-2 text-[10px] font-[700] text-[#9E9890] hover:text-[#1C1A17]"
              >
                {isEs ? 'Quitar' : 'Clear'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notas — textarea full-width */}
      <div className="mt-4">
        <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">
          {isEs ? 'Notas' : 'Notes'}
        </label>
        <textarea
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder={isEs ? 'Notas internas, especialidades, observaciones...' : 'Internal notes, specialties, observations...'}
          rows={3}
          className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B] resize-none"
        />
      </div>

      {/* Error message */}
      {formError && (
        <div className="mt-3 text-[11px] font-[700] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {formError}
        </div>
      )}

      {/* Cancel + Save buttons */}
      <div className="grid grid-cols-2 gap-3 mt-5">
        <button
          onClick={onClose}
          className="border border-[#ECEAE4] rounded-full py-2 text-[11px] font-[800] text-[#9E9890] hover:bg-[#F2F0EB]"
        >
          {isEs ? 'Cancelar' : 'Cancel'}
        </button>
        <button
          onClick={() => { setFormError(null); mutation.mutate() }}
          disabled={!form.name.trim() || mutation.isPending}
          className="bg-[#06539B] text-white rounded-full py-2 text-[11px] font-[800] hover:bg-[#14457E] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {mutation.isPending
            ? (isEs ? 'Guardando…' : 'Saving…')
            : editingId
              ? (isEs ? 'Actualizar' : 'Update')
              : (isEs ? 'Guardar' : 'Save')}
        </button>
      </div>
    </div>
  )
}
